import { useState, useMemo, useCallback } from 'react';
import { Palette, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { ColorSwatch } from './ColorSwatch';
import { ColorSearch } from './ColorSearch';
import { getColorByCode, DEFAULT_FEATURED_COLORS } from '@/data/sherwin-williams-colors';
import type { SherwinWilliamsColor } from '@/types/firestore';
import { cn } from '@/lib/utils';

interface ColorPickerProps {
  label: string;
  value?: SherwinWilliamsColor;
  onChange: (color: SherwinWilliamsColor | undefined) => void;
  featuredColorCodes?: string[];
  customColors?: SherwinWilliamsColor[];
  allowSearch?: boolean;
  required?: boolean;
  error?: string;
}

export function ColorPicker({
  label,
  value,
  onChange,
  featuredColorCodes = DEFAULT_FEATURED_COLORS,
  customColors = [],
  allowSearch = true,
  required = false,
  error,
}: ColorPickerProps) {
  const [showSearch, setShowSearch] = useState(false);

  // Helper to find color by code - checks custom colors first, then default colors
  const findColorByCode = useCallback((code: string): SherwinWilliamsColor | undefined => {
    // Check custom colors first (tenant-specific take priority)
    const customMatch = customColors.find((c) => c.code === code);
    if (customMatch) return customMatch;
    // Fall back to default colors
    return getColorByCode(code);
  }, [customColors]);

  // Memoize featured colors to prevent recalculation on every render
  const featuredColors = useMemo(
    () =>
      featuredColorCodes
        .map((code) => findColorByCode(code))
        .filter((c): c is SherwinWilliamsColor => c !== undefined),
    [featuredColorCodes, findColorByCode]
  );

  const handleSelect = (color: SherwinWilliamsColor) => {
    if (value?.code === color.code) {
      // Deselect if clicking the same color (only if not required)
      if (!required) {
        onChange(undefined);
      }
    } else {
      onChange(color);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Palette className="h-4 w-4 text-muted-foreground" />
          <Label className="text-sm font-medium">{label}</Label>
          {!required && (
            <span className="text-xs text-muted-foreground">(Optional)</span>
          )}
        </div>
        {value && (
          <div className="flex items-center gap-2 text-xs">
            <div
              className="w-4 h-4 rounded border border-border"
              style={{ backgroundColor: value.hex }}
            />
            <span className="text-muted-foreground">
              {value.name} ({value.code})
            </span>
          </div>
        )}
      </div>

      {/* Featured Colors Grid */}
      <div className="grid grid-cols-4 gap-3">
        {featuredColors.map((color) => (
          <ColorSwatch
            key={color.code}
            color={color}
            isSelected={value?.code === color.code}
            onClick={() => handleSelect(color)}
            size="md"
            showLabel
          />
        ))}
      </div>

      {/* Search Toggle & Panel */}
      {allowSearch && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowSearch(!showSearch)}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <Search className="h-4 w-4" />
            <span>Search all colors</span>
            {showSearch ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>

          {showSearch && (
            <ColorSearch
              onSelect={onChange}
              selectedColor={value}
              customColors={customColors}
              className="mt-2"
            />
          )}
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
